// frontend/src/Helpers/tts.js
// Based on your original file; only adds robot-speaking status reporting via /status/{job_id}
// and an optional onRobotSpeakingChange callback. No unrelated restyling.

export function startTTSEngine({
                                 idleFlushMs = 220,
                                 maxChunkChars = 140,
                                 url = "http://127.0.0.1:8877/speak",
                                 statusUrlBase = "http://127.0.0.1:8877/status",
                                 onRobotSpeakingChange = null,
                               } = {}) {
  let buffer = "";
  let idleTimer = null;

  // speaking ref-count (supports overlapping jobs)
  let speakingRefCount = 0;

  const setSpeaking = (v) => {
    if (onRobotSpeakingChange) {
      try {
        onRobotSpeakingChange(!!v);
      } catch (e) {
        console.warn("onRobotSpeakingChange callback failed", e);
      }
    }
  };

  const incSpeaking = () => {
    speakingRefCount += 1;
    if (speakingRefCount === 1) setSpeaking(true);
  };

  const decSpeaking = () => {
    speakingRefCount = Math.max(0, speakingRefCount - 1);
    if (speakingRefCount === 0) setSpeaking(false);
  };

  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

  const pollJobUntilFinished = async (jobId) => {
    let sawPlaying = false;

    try {
      for (;;) {
        let st = null;
        try {
          const resp = await fetch(`${statusUrlBase}/${encodeURIComponent(jobId)}`, {
            method: "GET",
            cache: "no-store",
          });
          if (!resp.ok) {
            // If status endpoint is temporarily unavailable, retry briefly
            await sleep(150);
            continue;
          }
          st = await resp.json();
        } catch (e) {
          // transient network error: retry
          await sleep(150);
          continue;
        }

        const state = st && st.state;

        if (state === "playing" && !sawPlaying) {
          sawPlaying = true;
          incSpeaking();
        }

        if (state === "done" || state === "error") {
          break;
        }

        await sleep(120);
      }
    } finally {
      if (sawPlaying) {
        // small tail to avoid immediate reopen while device drains buffers
        await sleep(200);
        decSpeaking();
      }
    }
  };

  const resetIdle = () => {
    if (idleTimer) clearTimeout(idleTimer);
    idleTimer = setTimeout(() => {
      // If user pauses, send whatever we have as a final “sentence”
      flush();
    }, idleFlushMs);
  };

  const postSentence = async (sentence) => {
    const text = (sentence || "").trim();
    if (!text) return;

    // Fire-and-forget; tester discards response body.
    // keepalive helps when tab is backgrounded / navigation happens.
    try {
      const resp = await fetch(url, {
        method: "POST",
        headers: {"Content-Type": "application/json"},
        body: JSON.stringify({text, lang: "auto"}), // force exactly like tester
        keepalive: true,
      });

      // If we can read job_id, poll status to know actual playback window.
      // NOTE: Your server returns JSON {"job_id": "...", ...}
      try {
        const data = await resp.json();
        const jobId = data && data.job_id;
        if (jobId) {
          // do not await; keep TTS path non-blocking
          pollJobUntilFinished(jobId);
        }
      } catch (e) {
        // If response isn't JSON or can't be read (keepalive / opaque), ignore
        console.warn("TTS response JSON parse failed", e);
      }
    } catch (e) {
      // Avoid throwing inside sagas; just log
      console.warn("TTS POST failed", e);
    }
  };

  // Split buffer into [completeSentences..., remainder]
  const extractCompleteSentences = (text) => {
    // Sentence boundary: ., !, ?, or newline. Keep it simple and robust.
    // This favors coherent language per sentence, matching your tester.
    const parts = [];
    let start = 0;

    for (let i = 0; i < text.length; i++) {
      const ch = text[i];
      if (ch === "." || ch === "!" || ch === "?" || ch === "\n") {
        const sentence = text.slice(start, i + 1);
        parts.push(sentence);
        start = i + 1;
      }
    }
    const remainder = text.slice(start);
    return {sentences: parts, remainder};
  };

  const flush = async () => {
    if (idleTimer) {
      clearTimeout(idleTimer);
      idleTimer = null;
    }

    const toSend = buffer.trim();
    buffer = "";

    if (!toSend) return;

    // If there are multiple sentences in the remainder, still send one per request.
    const {sentences, remainder} = extractCompleteSentences(toSend);
    for (const s of sentences) await postSentence(s);

    // If remainder exists (no terminal punctuation), send it as well on flush
    if (remainder.trim()) await postSentence(remainder);
  };

  const pushToken = async (token) => {
    const t = token ?? "";
    if (!t) return;

    buffer += t;

    // Hard cap to avoid infinite growth if no punctuation arrives:
    // when maxChunkChars is reached, flush what we have as “a sentence”
    if (buffer.length >= maxChunkChars) {
      // Try to send complete sentences first; otherwise send the whole buffer
      const {sentences, remainder} = extractCompleteSentences(buffer);

      // Send complete sentences immediately
      if (sentences.length) {
        for (const s of sentences) await postSentence(s);
        buffer = remainder;
      } else {
        // No punctuation yet; send it anyway to keep latency bounded
        await postSentence(buffer);
        buffer = "";
      }
    } else {
      // If we already have at least one complete sentence, send it right away
      const {sentences, remainder} = extractCompleteSentences(buffer);
      if (sentences.length) {
        for (const s of sentences) await postSentence(s);
        buffer = remainder;
      }
    }

    resetIdle();
  };

  return {
    pushToken,
    flush,
  };
}
